import { storeOverviewOptionalDisplay, storeOverviewOptionalTotals } from "./storeOverviewOptionalLy";

export type FocusedStoreSource = {
  label:string; target_uzs:number|null; net_sales_uzs:number; net_units:number; receipts:number;
  visitors:number|null; previous_year_sales_uzs:number; previous_year_units:number;
  previous_year_receipts?:number; revenue_share_percent:number;
};
const integer=new Intl.NumberFormat("tr-TR",{maximumFractionDigits:0});
const decimal=new Intl.NumberFormat("tr-TR",{minimumFractionDigits:2,maximumFractionDigits:2});
const oneDecimal=new Intl.NumberFormat("tr-TR",{minimumFractionDigits:1,maximumFractionDigits:1});
const percentage=(value:number|null)=>value==null?"—":oneDecimal.format(value)+"%";
const change=(value:number|null)=>value==null?"—":(value>=0?"+":"")+oneDecimal.format(value)+"%";

// Store screen only. Use the same raw sums and ratio-of-totals definitions as
// ReportTable, plus its existing optional-LY helper. Never average row ratios.
// Revenue shares keep their existing selected-period denominator, so their
// filtered total is the sum of the shares shown in the matching rows.
export function focusedStoreTotals(rows:readonly FocusedStoreSource[], benchmark:(label:string)=>number|null) {
  const sum=(key:"net_sales_uzs"|"net_units"|"receipts"|"visitors"|"target_uzs"|"previous_year_sales_uzs"|"previous_year_units"|"revenue_share_percent")=>rows.reduce((total,row)=>total+(row[key]??0),0);
  const sales=sum("net_sales_uzs"), units=sum("net_units"), receipts=sum("receipts"), target=sum("target_uzs"), visitors=sum("visitors");
  const lySales=sum("previous_year_sales_uzs"), lyUnits=sum("previous_year_units");
  const lyReceipts=rows.every(row=>row.previous_year_receipts!=null)?rows.reduce((total,row)=>total+row.previous_year_receipts!,0):null;
  const upt=receipts?units/receipts:0, lyUpt=lyReceipts?lyUnits/lyReceipts:0;
  const expected=target>0?rows.reduce((total,row)=>total+(row.target_uzs??0)*(benchmark(row.label)??100),0)/target:null;
  const optional=storeOverviewOptionalDisplay(storeOverviewOptionalTotals([...rows].map(row=>({...row,average_unit_price_uzs:row.net_units?row.net_sales_uzs/row.net_units:0})),{sales,units,receipts}));
  const display:Record<string,string|number|null>={
    ...optional,label:"",target:target>0?integer.format(target):"—",sales:integer.format(sales),
    targetRate:percentage(target?sales/target*100:null),__sort_targetRate:target?sales/target*100:null,__expected_targetRate:expected,
    upt:decimal.format(upt),cr:percentage(visitors?receipts/visitors*100:null),visitors:integer.format(visitors),
    receipts:integer.format(receipts),units:integer.format(units),basket:integer.format(receipts?sales/receipts:0),unitPrice:integer.format(units?sales/units:0),
    share:percentage(sum("revenue_share_percent")),lySales:lySales?integer.format(lySales):"—",
    salesChange:change(lySales?(sales-lySales)/Math.abs(lySales)*100:null),lyUpt:lyUpt?decimal.format(lyUpt):"—",
    uptChange:change(lyUpt?(upt-lyUpt)/lyUpt*100:null),
  };
  return {display,sales,units,receipts,target,visitors,expected};
}

// Additive Store Summary comparison. Aggregate the original sales and receipt
// counts before dividing; missing LY receipt counts are never inferred.
export function focusedStoreBasketDisplay(rows:readonly FocusedStoreSource[]):Record<string,string|number|null> {
  const sales=rows.reduce((sum,row)=>sum+row.net_sales_uzs,0);
  const receipts=rows.reduce((sum,row)=>sum+row.receipts,0);
  const previousSales=rows.reduce((sum,row)=>sum+row.previous_year_sales_uzs,0);
  const previousReceipts=rows.every(row=>row.previous_year_receipts!=null&&Number.isFinite(row.previous_year_receipts))
    ?rows.reduce((sum,row)=>sum+row.previous_year_receipts!,0):null;
  const currentBasket=receipts?sales/receipts:0;
  const previousBasket=previousReceipts!=null&&previousReceipts>0&&Number.isFinite(previousSales)
    ?previousSales/previousReceipts:null;
  const basketChange=previousBasket!=null&&previousBasket!==0
    ?(currentBasket-previousBasket)/Math.abs(previousBasket)*100:null;
  return {
    lyBasket:previousBasket==null?"—":integer.format(previousBasket),
    basketChange:change(basketChange),
    __sort_lyBasket:previousBasket,
    __sort_basketChange:basketChange,
  };
}

// Keep the same period/global-filter denominator while table search narrows
// the matching stores, just as the existing current revenue share does.
export function focusedStoreShareDisplay(rows:readonly FocusedStoreSource[],totalPreviousSales:number):Record<string,string|number|null> {
  const previousSales=rows.reduce((sum,row)=>sum+row.previous_year_sales_uzs,0);
  const lyShare=Number.isFinite(totalPreviousSales)&&totalPreviousSales!==0&&Number.isFinite(previousSales)
    ?previousSales/totalPreviousSales*100:null;
  return {lyShare:percentage(lyShare),__sort_lyShare:lyShare};
}
